import { useState } from 'react';
import type { BrandId } from '@nadoutong/contracts';
import { ExternalLink, Plus } from 'lucide-react';
import { useReceiptList, type ReceiptListScope } from '../api/receipts.ts';
import { formatLocalTimestamp } from './formatters.ts';
import { ReceiptDetailDrawer } from './ReceiptDetailDrawer.tsx';
import { ReceiptSubmitDialog } from './ReceiptSubmitDialog.tsx';
import { coerceErrorMessage } from './Toast.tsx';
import { useT } from '../i18n/index.ts';
import {
  RECEIPT_CHANNEL_LABEL_KEYS,
  RECEIPT_STATUS_LABEL_KEYS,
  formatReceiptMinor,
  receiptStatusChipTone,
} from '../lib/receipts.ts';
import {
  Button,
  Chip,
  EmptyState,
  Panel,
  Table,
  TableEmpty,
  TableSkeleton,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * Customer 360 receipts tab (epic #1031 E2-R5, #1063): this customer's
 * receipt history (server row-scoped like every receipt list — a CM
 * still only sees their own submissions here) plus the pinned-customer
 * entry point into the submit dialog. Statuses come back verbatim, so
 * the CM watches submitted → credited/rejected without leaving the 360.
 */

const PAGE_SIZE = 50;

interface CustomerReceiptsSectionProps {
  readonly orgId: string;
  readonly customerEmail: string;
  /** Signup brand of the pinned customer — drives the invoice fields (#1064). */
  readonly customerBrand: BrandId;
  /**
   * #1958: submitting a receipt is RECEIPT_SUBMIT_ROLES (admin + customer
   * manager), which is exactly the 360 page's `canWrite`. A finance session
   * still reads the history here and still decides receipts from the receipt
   * console — that decision lives on `/v1/ops/receipts/*`, not on this button.
   */
  readonly canSubmit: boolean;
}

export function CustomerReceiptsSection({
  orgId,
  customerEmail,
  customerBrand,
  canSubmit,
}: CustomerReceiptsSectionProps): JSX.Element {
  const t = useT();
  const [submitOpen, setSubmitOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const scope: ReceiptListScope = {
    limit: PAGE_SIZE,
    offset: 0,
    status: '',
    brand: '',
    orgId,
    company: '',
    invoiceStatus: '',
  };
  const query = useReceiptList(scope, { enabled: true });
  const rows = query.data?.receipts ?? [];

  return (
    <Panel
      caption={t('receipts.customer_section.title')}
      count={query.data?.total}
      testId="customer-receipts-section"
      actions={
        canSubmit ? (
          <Button
            size="sm"
            variant="primary"
            onClick={() => setSubmitOpen(true)}
            data-testid="customer-receipts-record"
          >
            <Plus aria-hidden="true" className="h-3.5 w-3.5" />
            {t('receipts.entry.open')}
          </Button>
        ) : undefined
      }
    >
      {query.isPending ? <TableSkeleton rows={3} testId="customer-receipts-loading" /> : null}
      {query.isError ? (
        <EmptyState
          title={t('receipts.load_error')}
          hint={coerceErrorMessage(query.error)}
          action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
          testId="customer-receipts-error"
        />
      ) : null}
      {query.isSuccess ? (
        <Table testId="customer-receipts-table" className="rounded-none border-0">
          <THead>
            <Tr interactive={false}>
              <Th>{t('receipts.col.status')}</Th>
              <Th num>{t('receipts.col.amount')}</Th>
              <Th>{t('receipts.col.channel')}</Th>
              <Th>{t('receipts.col.received_at')}</Th>
              <Th>{t('receipts.col.submitted_at')}</Th>
              <Th className="text-right">{t('receipts.col.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.length === 0 ? (
              <TableEmpty colSpan={6} testId="customer-receipts-empty">
                {t('receipts.customer_section.empty')}
              </TableEmpty>
            ) : (
              rows.map((receipt) => (
                <Tr key={receipt.id} data-testid={`customer-receipt-row-${receipt.id}`}>
                  <Td>
                    <Chip tone={receiptStatusChipTone(receipt.status)}>
                      {t(RECEIPT_STATUS_LABEL_KEYS[receipt.status])}
                    </Chip>
                  </Td>
                  <Td num emphasis>
                    {formatReceiptMinor(receipt.amount_minor, receipt.currency)}{' '}
                    {receipt.currency}
                  </Td>
                  <Td>{t(RECEIPT_CHANNEL_LABEL_KEYS[receipt.channel])}</Td>
                  <Td mono>{formatLocalTimestamp(receipt.received_at)}</Td>
                  <Td mono>{formatLocalTimestamp(receipt.submitted_at)}</Td>
                  <Td className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDetailId(receipt.id)}
                      data-testid={`customer-receipt-detail-${receipt.id}`}
                    >
                      <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
                      {t('receipts.action.detail')}
                    </Button>
                  </Td>
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      ) : null}
      {canSubmit ? (
        <ReceiptSubmitDialog
          open={submitOpen}
          onOpenChange={setSubmitOpen}
          orgId={orgId}
          customerEmail={customerEmail}
          customerBrand={customerBrand}
        />
      ) : null}
      <ReceiptDetailDrawer
        receiptId={detailId}
        onOpenChange={(open) => {
          if (!open) {
            setDetailId(null);
          }
        }}
      />
    </Panel>
  );
}
